import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  type Layer,
  type Program,
  inferStateFromZip,
  midAmount,
  VERIFY_WINDOW_MS,
  buildGrantStackAiPrompt,
} from "./grantStack.server";
import { foreignTextProblem, hasForeignText } from "../../supabase/functions/_shared/foreignText";

/**
 * Members frequently type non-employers into the employer box ("retired",
 * "ssdi", "none"). Searching the employer layer for those returns nothing and
 * can cache a junk-keyed result, so treat them as "no employer given".
 */
const JUNK_EMPLOYERS = new Set([
  "retired", "none", "n/a", "na", "no", "n", "nope", "nothing", "me",
  "ssdi", "ssi", "social security", "disability", "disabled",
  "self", "self employed", "self-employed", "unemployed", "not employed",
  "unknown", "idk", "i don't know", "dont know", "don't know",
  "stay at home", "stay-at-home", "homemaker", "student", "retiree",
]);
const cleanEmployer = (value: string): string => {
  const t = value.trim();
  if (t.length < 3) return "";
  return JUNK_EMPLOYERS.has(t.toLowerCase()) ? "" : t;
};

export const grantStack = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { zipCode: string; employerName?: string }) => d)
  .handler(async ({ data, context }) => {
    const { supabaseAdmin: svc } = await import("@/integrations/supabase/client.server");
    const user = { id: context.userId };

    const zip: string = (data.zipCode || "").toString();
    const employerNameInput: string = cleanEmployer((data.employerName || "").toString());

    if (!/^\d{5}$/.test(zip)) {
      throw new Error("Please provide a valid 5-digit zip code.");
    }

    // Resolve employer from profile if not passed
    let employerName = employerNameInput;
    if (!employerName) {
      const { data: ua } = await svc
        .from("user_attributes")
        .select("employer_name")
        .eq("user_id", user.id)
        .maybeSingle();
      employerName = cleanEmployer(((ua as any)?.employer_name || "").toString());
    }

    const state = inferStateFromZip(zip);
    const cacheKey = `${zip}|${(employerName || "").toLowerCase()}`;

    // Cache check
    const { data: cached } = await svc
      .from("grant_lookup_cache")
      .select("result, expires_at")
      .eq("cache_key", cacheKey)
      .maybeSingle();

    if (cached && new Date((cached as any).expires_at) > new Date()) {
      return (cached as any).result;
    }

    // 1. Curated layers from DB
    const { data: dbPrograms } = await svc
      .from("grant_programs")
      .select("*")
      .eq("is_active", true)
      .neq("freshness_state", "dead")
      .or(
        `layer.eq.federal,and(layer.eq.state,state.eq.${state || "ZZ"}),and(layer.eq.county,state.eq.${state || "ZZ"}),zip_codes.cs.{${zip}}`
      );

    const layers: Record<Layer, Program[]> = { federal: [], state: [], county: [], employer: [] };
    for (const p of (dbPrograms as any[]) || []) {
      // Filter county by zip overlap if zip_codes set
      if (p.layer === "county" && (p.zip_codes?.length ?? 0) > 0 && !p.zip_codes.includes(zip)) continue;
      const verifiedRecently =
        !!p.admin_verified_at &&
        Date.now() - new Date(p.admin_verified_at).getTime() < VERIFY_WINDOW_MS;
      layers[p.layer as Layer].push({
        id: p.id,
        layer: p.layer,
        name: p.name,
        provider: p.provider,
        amount_text: p.amount_text,
        amount_min: p.amount_min,
        amount_max: p.amount_max,
        eligibility_summary: p.eligibility_summary,
        stackable_with: p.stackable_with || [],
        url: p.url,
        source: p.source,
        verified: verifiedRecently && p.freshness_state === "fresh",
      });
    }

    // 2. Employer layer
    let employerStatus: "verified" | "inferred" | "unknown" = "unknown";
    if (employerName) {
      const { data: empBenefits } = await svc
        .from("employer_benefits")
        .select("*")
        .eq("is_active", true)
        .ilike("employer_name", employerName);
      if (empBenefits && empBenefits.length) {
        employerStatus = ((empBenefits as any)[0].status as any) || "verified";
        for (const b of empBenefits as any[]) {
          layers.employer.push({
            layer: "employer",
            name: b.benefit_type === "down-payment-assist" ? `${employerName} Down Payment Benefit` : `${employerName} ${b.benefit_type}`,
            provider: employerName,
            amount_text: b.amount_text,
            amount_min: b.amount_min,
            amount_max: b.amount_max,
            eligibility_summary: b.eligibility,
            url: b.url,
            source: "admin",
            verified: b.status === "verified",
          });
        }
      }
    }

    // 3. AI fallback for missing layers
    const missing: Layer[] = [];
    if (layers.federal.length === 0) missing.push("federal");
    if (layers.state.length === 0 && state) missing.push("state");
    if (layers.county.length === 0) missing.push("county");
    if (employerName && layers.employer.length === 0) missing.push("employer");

    if (missing.length) {
      const LOVABLE_API_KEY = process.env['LOVABLE_API_KEY'];
      if (LOVABLE_API_KEY) {
        try {
          const prompt = buildGrantStackAiPrompt(zip, state, employerName, missing);

          const aiRes = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
            method: "POST",
            headers: { Authorization: `Bearer ${LOVABLE_API_KEY}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              model: "google/gemini-2.5-flash",
              messages: [
                { role: "system", content: "Return raw JSON only, no markdown. Write every field in plain American English using only English letters: never include words or characters from any other language." },
                { role: "user", content: prompt },
              ],
            }),
          });
          if (aiRes.ok) {
            const aiJson = await aiRes.json();
            const raw = aiJson.choices?.[0]?.message?.content || "";
            const cleaned = raw.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
            try {
              const parsed = JSON.parse(cleaned);
              for (const l of missing) {
                const arr = Array.isArray(parsed[l]) ? parsed[l] : [];
                for (const item of arr) {
                  if (!item?.name) continue;
                  // US English only: drop a program whose text drifted into another language or came back garbled,
                  // before it is shown, cached or saved as a candidate.
                  if (hasForeignText(item.name, item.provider, item.eligibility_summary, item.amount_text, item.served_area)) {
                    console.warn(
                      "grantStack: skipped a program with non-English or garbled text",
                      foreignTextProblem(item.name) ?? foreignTextProblem(item.eligibility_summary) ?? foreignTextProblem(item.provider),
                    );
                    continue;
                  }
                  layers[l].push({
                    layer: l,
                    name: item.name,
                    provider: item.provider || null,
                    amount_text: item.amount_text || null,
                    amount_min: item.amount_min ?? null,
                    amount_max: item.amount_max ?? null,
                    eligibility_summary: item.eligibility_summary || null,
                    url: item.url || null,
                    source: "ai",
                    verified: false,
                  });
                  // Persist as candidate for admin review
                  await svc.from("grant_programs").insert({
                    layer: l,
                    name: item.name,
                    provider: item.provider || null,
                    state: l === "federal" ? null : state,
                    zip_codes: l === "county" ? [zip] : [],
                    employer_name: l === "employer" ? employerName : null,
                    amount_text: item.amount_text || null,
                    amount_min: item.amount_min ?? null,
                    amount_max: item.amount_max ?? null,
                    eligibility_summary: item.eligibility_summary || null,
                    url: item.url || null,
                    source: "ai",
                    is_active: true,
                  } as any);
                }
              }
              if (employerName && layers.employer.length === 0) employerStatus = "unknown";
              else if (employerName && employerStatus === "unknown") employerStatus = "inferred";
            } catch (e) {
              console.error("AI parse failed", e, cleaned);
            }
          }
        } catch (e) {
          console.error("AI fallback error", e);
        }
      }
    }

    // 4. Totals
    const totalEstimated =
      midAmount({ amount_min: null, amount_max: 0, layer: "federal", name: "", source: "admin" }) +
      [...layers.federal, ...layers.state, ...layers.county, ...layers.employer].reduce(
        (acc, p) => acc + midAmount(p),
        0
      );

    // Most programs (all federal, all state, most county) publish no dollar
    // figure, so a $0 total means "amounts unpublished", not "nothing found".
    // Surface the count so the UI can lead with programs instead of a $0.
    const programCount =
      layers.federal.length + layers.state.length + layers.county.length + layers.employer.length;

    const result = {
      zip,
      state,
      employer_name: employerName || null,
      employer_status: employerStatus,
      layers,
      total_estimated: totalEstimated,
      program_count: programCount,
      has_amounts: totalEstimated > 0,
      generated_at: new Date().toISOString(),
    };


    // 5. Cache
    await svc.from("grant_lookup_cache").upsert({
      cache_key: cacheKey,
      zip,
      employer_name: employerName || null,
      result,
      created_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
    } as any);

    return result;
  });
